/**
 * Разметка schema.org для витрины.
 *
 * Собрана в одном месте и чистыми функциями: раньше JSON-LD жил прямо в
 * layouts/default.vue, и добавить к нему страницы меню и хлебные крошки значило
 * дописать ещё сотню строк в лейаут, который и без того отвечает за всё сразу.
 *
 * Отдаём @graph, а не один объект: у страницы несколько сущностей (сайт,
 * заведение, каждый филиал, крошки), и связывать их нужно ссылками по @id —
 * иначе поисковик считает их независимыми и не понимает, что филиалы
 * принадлежат тому же ресторану, что и меню.
 */

/** Узел @graph. Форма произвольная — её задаёт schema.org, а не мы. */
export type JsonLdNode = Record<string, unknown>

const WEEK_DAYS = [
  'Monday',
  'Tuesday',
  'Wednesday',
  'Thursday',
  'Friday',
  'Saturday',
  'Sunday',
]

const FULL_DAY_HINTS = new Set(['24/7', 'круглосуточно', '24h', 'always', 'круглые сутки'])

/**
 * Часы работы филиала в формате schema.org.
 *
 * В БД это одна строка на все дни: «круглосуточно» или «10:00-22:00»
 * (см. parseWorkingHours в useWorkingHours). Разворачиваем её в спецификацию
 * на семь дней — именно из неё Google собирает строку «Открыто до 22:00».
 */
export function openingHoursSpecification(hours?: string | null): JsonLdNode[] | null {
  const raw = (hours || '').trim().toLowerCase()
  if (!raw) return null

  const isFullDay = FULL_DAY_HINTS.has(raw)
  let opens = '00:00'
  let closes = '23:59'

  if (!isFullDay) {
    const parts = raw.split(/[–—-]/)
    if (parts.length !== 2) return null
    const normalize = (value: string) => {
      const match = value.trim().match(/^(\d{1,2})[.:](\d{2})$/)
      if (!match) return null
      const hh = Number(match[1])
      const mm = Number(match[2])
      if (hh > 23 || mm > 59) return null
      return `${String(hh).padStart(2, '0')}:${String(mm).padStart(2, '0')}`
    }
    const from = normalize(parts[0])
    const to = normalize(parts[1])
    if (!from || !to) return null
    opens = from
    closes = to
  }

  // Смена через полночь («10:00-00:00», «20:00-04:00») в одной спецификации
  // не выражается: closes должен быть позже opens, иначе интервал читается как
  // пустой. Закрываем сутками — ближе к правде, чем «закрыто весь день».
  if (closes <= opens) closes = '23:59'

  return [{ '@type': 'OpeningHoursSpecification', dayOfWeek: WEEK_DAYS, opens, closes }]
}

/** Убирает пустые значения: schema.org игнорирует их, а размер разметки растёт. */
function compact<T extends JsonLdNode>(node: T): T {
  for (const key of Object.keys(node)) {
    const value = node[key]
    if (value == null || value === '' || (Array.isArray(value) && value.length === 0)) {
      delete node[key]
    }
  }
  return node
}

/**
 * Имя точки на карте.
 *
 * Менеджеры называют филиалы по-разному: то «на Советской 63/1», то
 * «Шаурмен на Советской 63/1». Приклеивать бренд вслепую нельзя — получалось
 * «Шаурмен — Шаурмен на Советской 63/1».
 */
function branchName(institutionName: string, name?: string | null): string {
  const own = (name || '').trim()
  if (!own) return institutionName
  return own.toLowerCase().includes(institutionName.trim().toLowerCase())
    ? own
    : `${institutionName} — ${own}`
}

function postalAddress(branch: any): JsonLdNode | null {
  const address = branch?.address
  if (!address?.street && !address?.city) return null
  return compact({
    '@type': 'PostalAddress',
    addressCountry: 'RU',
    addressLocality: address?.city,
    streetAddress: address?.street,
  })
}

function geoCoordinates(branch: any): JsonLdNode | null {
  const latitude = Number(branch?.latitude)
  const longitude = Number(branch?.longitude)
  if (!Number.isFinite(latitude) || !Number.isFinite(longitude)) return null
  if (!latitude && !longitude) return null
  return { '@type': 'GeoCoordinates', latitude, longitude }
}

/**
 * Ценовой диапазон меню — «300–900 ₽».
 *
 * Считаем по реальным ценам витрины: заводить его руками в админке значит
 * гарантированно разойтись с меню после первой же переоценки.
 */
export function priceRangeOf(categories: any[]): string {
  const prices: number[] = []
  for (const category of categories ?? []) {
    for (const product of category?.products ?? []) {
      const value = Number(product?.price)
      if (Number.isFinite(value) && value > 0) prices.push(value)
    }
  }
  if (prices.length === 0) return ''
  const min = Math.min(...prices)
  const max = Math.max(...prices)
  return min === max ? `${min} ₽` : `${min}–${max} ₽`
}

/** Первая картинка товара — в разметку идёт одна, галерея поисковику не нужна. */
function productImage(product: any): string | undefined {
  return Array.isArray(product?.img) ? product.img[0]?.url : undefined
}

/**
 * Тип кухни для карточки заведения.
 *
 * Отдельное поле админки (seo_meta.cuisine), а НЕ keywords: в keywords лежит
 * список поисковых запросов, и в servesCuisine он читается как спам. Строку
 * «шаурма, бургеры» разворачиваем в массив — schema.org ждёт перечисление,
 * а не одну склеенную строку.
 */
export function servesCuisine(raw?: string | null): string[] {
  return (raw || '')
    .split(',')
    .map(value => value.trim())
    .filter(Boolean)
}

/** Меню целиком: Menu → MenuSection → MenuItem с ценами и фото. */
export function menuNode(categories: any[]): JsonLdNode | null {
  const sections = (categories ?? [])
    .map((category: any) => {
      const items = (category?.products ?? [])
        .map((product: any) => {
          const name = product?.name || product?.external_name
          if (!name) return null
          return compact({
            '@type': 'MenuItem',
            name,
            description: product?.description,
            image: productImage(product),
            offers:
              product?.price == null
                ? undefined
                : {
                    '@type': 'Offer',
                    price: String(product.price),
                    priceCurrency: 'RUB',
                  },
          })
        })
        .filter(Boolean)
      if (!category?.category || items.length === 0) return null
      return { '@type': 'MenuSection', name: category.category, hasMenuItem: items }
    })
    .filter(Boolean)

  return sections.length > 0 ? { '@type': 'Menu', hasMenuSection: sections } : null
}

export interface RestaurantGraphInput {
  origin: string
  institution: any
  /** Филиал, выбранный гостем: его адрес и телефон идут в основную карточку. */
  selectedBranch?: any
  /** Все филиалы заведения — каждый становится своей точкой на карте. */
  branches?: any[]
  categories?: any[]
  image?: string
  cuisine?: string | null
  /** Меню тяжёлое (сотня позиций), поэтому кладём его только там, где оно и есть. */
  withMenu?: boolean
}

/**
 * Сайт + заведение + филиалы одним связанным графом.
 *
 * Филиалы отдельными узлами Restaurant, а не адресами внутри одного: у каждого
 * свой адрес, телефон и часы, и на карте они должны быть разными точками.
 * Связь — branchOf по @id головного заведения.
 */
export function buildRestaurantGraph(input: RestaurantGraphInput): JsonLdNode[] {
  const { origin, institution } = input
  if (!institution?.name) return []

  const restaurantId = `${origin}/#restaurant`
  const nodes: JsonLdNode[] = []

  const restaurant: JsonLdNode = {
    '@type': 'Restaurant',
    '@id': restaurantId,
    name: institution.name,
    url: origin,
    image: input.image,
    servesCuisine: servesCuisine(input.cuisine),
    priceRange: priceRangeOf(input.categories ?? []),
    currenciesAccepted: 'RUB',
  }

  const selected = input.selectedBranch
  if (selected?.phone) restaurant.telephone = selected.phone
  const address = postalAddress(selected)
  if (address) restaurant.address = address
  const geo = geoCoordinates(selected)
  if (geo) restaurant.geo = geo
  const hours = openingHoursSpecification(selected?.working_hours?.hours)
  if (hours) restaurant.openingHoursSpecification = hours

  if (input.withMenu) {
    const menu = menuNode(input.categories ?? [])
    if (menu) restaurant.hasMenu = menu
  }

  nodes.push(
    compact(restaurant),
    compact({
      '@type': 'WebSite',
      '@id': `${origin}/#website`,
      url: origin,
      name: institution.name,
      inLanguage: 'ru-RU',
      publisher: { '@id': restaurantId },
    }),
  )

  // Филиалы. Тот, что уже описан в основной карточке, повторять незачем.
  for (const branch of input.branches ?? []) {
    const branchAddress = postalAddress(branch)
    if (!branchAddress) continue
    if (selected?.id != null && branch?.id === selected.id) continue

    nodes.push(
      compact({
        '@type': 'Restaurant',
        '@id': `${origin}/#branch-${branch.id}`,
        name: branchName(institution.name, branch?.name),
        branchOf: { '@id': restaurantId },
        url: origin,
        image: input.image,
        telephone: branch?.phone,
        address: branchAddress,
        geo: geoCoordinates(branch),
        openingHoursSpecification: openingHoursSpecification(branch?.working_hours?.hours),
        servesCuisine: servesCuisine(input.cuisine),
        priceRange: priceRangeOf(input.categories ?? []),
      }),
    )
  }

  return nodes
}

export interface BreadcrumbItem {
  name: string
  /** Путь от корня («/menu/burgers»). У последнего элемента можно опустить. */
  path?: string
}

/**
 * Хлебные крошки для страницы.
 *
 * Google рисует ими путь вместо голого URL в сниппете, а заодно понимает
 * иерархию каталога — по одному только адресу /dish/... он её не выводит.
 */
export function buildBreadcrumbs(origin: string, items: BreadcrumbItem[]): JsonLdNode | null {
  if (items.length === 0) return null
  return {
    '@type': 'BreadcrumbList',
    itemListElement: items.map((item, index) =>
      compact({
        '@type': 'ListItem',
        position: index + 1,
        name: item.name,
        item: item.path ? `${origin}${item.path}` : undefined,
      }),
    ),
  }
}

/** Готовый JSON для <script type="application/ld+json">. */
export function graphToJson(nodes: JsonLdNode[]): string {
  if (nodes.length === 0) return ''
  return JSON.stringify({ '@context': 'https://schema.org', '@graph': nodes })
}
